import { logError } from '@ardoq/logging';

type ExtraData = {
  [key: string | number]: unknown;
};

class InvariantError extends Error {
  extraData: ExtraData;
  constructor(message: string, extraData?: ExtraData) {
    super(message);
    this.name = 'InvariantError';
    this.extraData = extraData || {};
  }
}

export const isInvariantError = (error: any): error is InvariantError => {
  return error instanceof InvariantError;
};

/**
 * Captures an invariant error and logs it as an error, returning a default value
 * to allow the program to continue.
 * This is useful where we are introducing invariants instead of defaulting to `null`,
 * allowing us to move the boundary up the stack and track their occurrence.
 */
export const captureInvariant = <T>(fn: () => T, defaultValue: T) => {
  try {
    return fn();
  } catch (error) {
    if (isInvariantError(error)) {
      logError(error, null, error.extraData);
      return defaultValue;
    }

    // re-throw unexpected errors
    throw error;
  }
};

/**
 * Asserts that the `condition` is truthy and narrows the type, throws an error otherwise.
 * Runs in all production, test, and development mode.
 */
export function invariant(
  value: boolean,
  message: string,
  extraData?: ExtraData
): asserts value;
export function invariant<T>(
  value: T | false | null | undefined,
  message: string,
  extraData?: ExtraData
): asserts value is T;
export function invariant(value: any, message: string, extraData?: ExtraData) {
  // typeof used for robustness in case someone overwrites the global `undefined`
  if (value === false || value === null || typeof value === 'undefined') {
    throw new InvariantError(message, extraData);
  }
}
